import { authFetch } from '../../auth/authFetch';
import { getApiBaseUrl } from '../../auth/api';
import type {
  ApiResponse,
  CreateWorkersCompJurisdictionRequestDto,
  RenewWorkersCompJurisdictionRequestDto,
  SaveWicEntryRequestDto,
  UpdateWorkersCompJurisdictionRequestDto,
  WorkersCompJurisdictionCollectionDto,
  WorkersCompJurisdictionDto,
} from './organisationApiTypes';

const base = (businessRecordId: string) =>
  `${getApiBaseUrl()}/api/business-records/${encodeURIComponent(businessRecordId)}/workers-compensation`;

const parse = async <T>(response: Response): Promise<T> => {
  const body = await response.json().catch(() => null) as ApiResponse<T> | null;
  if (!response.ok) throw new Error(body?.message || `Request failed: ${response.status}`);
  if (!body?.success || body.result == null)
    throw new Error(body?.message || 'The server returned an unreadable workers compensation response.');
  return body.result;
};

const json = (method: 'POST' | 'PUT', body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

const policyUrl = (businessRecordId: string, workersCompJurisdictionId: string) =>
  `${base(businessRecordId)}/${encodeURIComponent(workersCompJurisdictionId)}`;

export const getWorkersCompJurisdictions = async (businessRecordId: string): Promise<WorkersCompJurisdictionCollectionDto> =>
  parse(await authFetch(base(businessRecordId)));

export const getWorkersCompJurisdiction = async (businessRecordId: string, workersCompJurisdictionId: string): Promise<WorkersCompJurisdictionDto> =>
  parse(await authFetch(policyUrl(businessRecordId, workersCompJurisdictionId)));

export const createWorkersCompJurisdiction = async (businessRecordId: string, request: CreateWorkersCompJurisdictionRequestDto): Promise<WorkersCompJurisdictionDto> =>
  parse(await authFetch(base(businessRecordId), json('POST', request)));

export const updateWorkersCompJurisdiction = async (businessRecordId: string, workersCompJurisdictionId: string, request: UpdateWorkersCompJurisdictionRequestDto): Promise<WorkersCompJurisdictionDto> =>
  parse(await authFetch(policyUrl(businessRecordId, workersCompJurisdictionId), json('PUT', request)));

export const renewWorkersCompJurisdiction = async (businessRecordId: string, workersCompJurisdictionId: string, request: RenewWorkersCompJurisdictionRequestDto): Promise<WorkersCompJurisdictionDto> =>
  parse(await authFetch(`${policyUrl(businessRecordId, workersCompJurisdictionId)}/renew`, json('POST', request)));

export const saveWicEntry = async (businessRecordId: string, workersCompJurisdictionId: string, request: SaveWicEntryRequestDto): Promise<WorkersCompJurisdictionDto> =>
  parse(await authFetch(`${policyUrl(businessRecordId, workersCompJurisdictionId)}/wic-entries`, json('POST', request)));

export const updateWicEntry = async (businessRecordId: string, workersCompJurisdictionId: string, wicEntryId: string, request: SaveWicEntryRequestDto): Promise<WorkersCompJurisdictionDto> =>
  parse(await authFetch(`${policyUrl(businessRecordId, workersCompJurisdictionId)}/wic-entries/${encodeURIComponent(wicEntryId)}`, json('PUT', request)));

export const deleteWicEntry = async (businessRecordId: string, workersCompJurisdictionId: string, wicEntryId: string): Promise<WorkersCompJurisdictionDto> =>
  parse(await authFetch(`${policyUrl(businessRecordId, workersCompJurisdictionId)}/wic-entries/${encodeURIComponent(wicEntryId)}`, { method: 'DELETE' }));
